/**
 * TD-2772 — placement geometry for the heatmap tile context menu
 * (`.icon-button-tooltip-context-link-menu`, opened by right-click or long press).
 *
 * Its own module so the maths stays pure and unit-testable: the menu lives inside
 * ProfilerHeatmap, which drags in d3 and the store, and a DOM-free function is also
 * the only way to pin this behaviour — jsdom reports every box as 0×0.
 */

/*
 * Box geometry as authored in `buttons.less`: 8px padding top and bottom plus a 1px
 * border either side (18), a 4px row gap, a first row carrying the company-name
 * subline (4 + 19 + 10 + 4 = 37) and a plain second row (4 + 19 + 4 = 27).
 *
 * Derived rather than measured on purpose. Measuring needs a layout pass, so the menu
 * either flashes at the wrong spot for a frame or has to be painted hidden first; and
 * the subline cannot wrap (`white-space: nowrap` + ellipsis), so the box really is
 * deterministic from the row count. The old code assumed a flat 60px — about right
 * for one row and ~26px short for two, which is why the second row used to land in
 * the band occupied by the mobile action bar.
 */
const CONTEXT_MENU_CHROME_HEIGHT = 18;
const CONTEXT_MENU_ROW_GAP = 4;
const CONTEXT_MENU_ROW_WITH_SUBLINE = 37;
const CONTEXT_MENU_ROW_PLAIN = 27;

/** Widest the menu gets; `.context-link-text`'s subline is capped and cannot wrap. */
export const CONTEXT_MENU_WIDTH = 200;

/** Gap kept between the menu and every viewport edge. */
export const CONTEXT_MENU_MARGIN = 10;

export const getContextMenuHeight = (hasExclusionRow) =>
  CONTEXT_MENU_CHROME_HEIGHT +
  CONTEXT_MENU_ROW_WITH_SUBLINE +
  (hasExclusionRow ? CONTEXT_MENU_ROW_GAP + CONTEXT_MENU_ROW_PLAIN : 0);

/**
 * Place the menu at the tap/click point, keeping the whole box on screen and clear of
 * whatever is pinned to the bottom (`bottomInset` — the mobile results action bar).
 *
 * Near the bottom edge it flips *above* the point rather than sliding up over it, so
 * the tile the analyst just pressed stays visible.
 */
export const clampContextMenuPosition = ({
  x,
  y,
  menuWidth,
  menuHeight,
  viewportWidth,
  viewportHeight,
  bottomInset = 0,
  margin = CONTEXT_MENU_MARGIN
}) => {
  let left = x;
  if (left + menuWidth > viewportWidth) {
    left = viewportWidth - menuWidth - margin;
  }
  if (left < margin) {
    left = margin;
  }

  const bottomLimit = viewportHeight - bottomInset - margin;
  let top = y;
  if (top + menuHeight > bottomLimit) {
    const flipped = y - menuHeight - margin;
    top = flipped >= margin ? flipped : Math.max(margin, bottomLimit - menuHeight);
  }
  if (top < margin) {
    top = margin;
  }

  return { x: left, y: top };
};
